import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import {
  AlertOctagon,
  AlertTriangle,
  Camera,
  CheckCircle2,
  Cpu,
  Globe,
  Hourglass,
  PizzaIcon,
  ServerCog,
  Timer,
  TriangleAlert,
} from "lucide-react";
import { Card } from "@/components/ui/card";
import { useLocalization } from "@/context/localization-context";
import { cn } from "@/lib/utils";
import {
  errorMessage,
  useMonitoringLive,
  useMonitoringMetrics,
  useMonitoringPipeline,
  useMonitoringSummary,
} from "../api";
import { TimeSeriesChart } from "../components/time-series-chart";
import {
  Ago,
  EmptyBlock,
  ErrorBlock,
  FetchingDot,
  LevelBadge,
  LoadingBlock,
  SectionCard,
} from "../components/primitives";
import { FindingRow } from "../findings";
import {
  formatCount,
  formatDuration,
  formatMb,
  formatMs,
  formatMsTick,

  formatClock,
  DASH,
} from "../format";
import { useMonitoring, type MonitoringTab } from "../monitoring-context";
import { LEVEL_COLORS, SERIES, SERIES_LIGHT, NEUTRAL, VERDICT_COLORS, STATUS } from "../palette";
import type { PipelinePizza, Verdict } from "../types";

export function OverviewTab() {
  return (
    <div className="flex flex-col gap-4">
      <StatusBanner />
      <KpiGrid />
      <div className="grid grid-cols-1 gap-4 2xl:grid-cols-2">
        <FlowChart />
        <VerdictLatencyChart />
        <PodLatencyChart />
        <EventsChart />
      </div>
      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        <RecentCaptures />
        <RecentEvents />
      </div>
    </div>
  );
}

// --- status + findings -------------------------------------------------------------------------------------
function StatusBanner() {
  const { t, tp } = useLocalization();
  const { setTab } = useMonitoring();
  const summary = useMonitoringSummary();

  if (summary.isLoading) return <LoadingBlock height={120} />;
  if (summary.isError && !summary.data) {
    return <ErrorBlock message={errorMessage(summary.error)} onRetry={() => summary.refetch()} />;
  }
  const data = summary.data;
  if (!data) return null;

  const problems = data.findings.filter((f) => f.severity !== "info");
  const infos = data.findings.filter((f) => f.severity === "info");
  const Icon = data.status === "critical" ? AlertOctagon : data.status === "warning" ? AlertTriangle : CheckCircle2;

  return (
    <Card
      className={cn(
        "overflow-hidden",
        data.status === "critical" && "border-red-500/40",
        data.status === "warning" && "border-amber-500/30",
      )}
    >
      <div
        className={cn(
          "flex flex-wrap items-center gap-3 px-4 py-4",
          data.status === "critical" && "bg-red-500/10",
          data.status === "warning" && "bg-amber-500/5",
        )}
      >
        <Icon
          className={cn(
            "h-8 w-8 shrink-0",
            data.status === "critical" ? "text-red-400" : data.status === "warning" ? "text-amber-400" : "text-green-400/80",
          )}
          aria-hidden
        />
        <div className="min-w-0 flex-1">
          <div className="text-lg font-semibold leading-tight">{t(`monitoring.status.${data.status}`)}</div>
          <div className="mt-0.5 text-xs text-muted-foreground">
            {data.status === "ok" ? t("monitoring.status.okDetail") : tp("monitoring.status.findings", problems.length)}
            {" · "}
            {t("monitoring.status.checkedAt", { ago: "" }).trim()} <Ago ts={data.generated_at} />
          </div>
        </div>
        <FetchingDot active={summary.isFetching} />
      </div>
      {Object.entries(data.section_errors ?? {}).map(([section, error]) => (
        <div key={section} className="border-t px-4 py-2 text-xs text-amber-300">
          {t("monitoring.status.sectionError", { section, error })}
        </div>
      ))}
      {(problems.length > 0 || infos.length > 0) && (
        <div className="divide-y border-t px-2 py-1">
          {[...problems, ...infos].map((finding, i) => (
            <FindingRow key={`${finding.code}-${finding.subject ?? i}`} finding={finding} onOpen={setTab} />
          ))}
        </div>
      )}
    </Card>
  );
}

// --- KPI tiles ----------------------------------------------------------------------------------------------
type Tone = "ok" | "warn" | "nok" | "neutral";

function KpiTile({
  icon: Icon,
  label,
  value,
  detail,
  tone = "neutral",
  tab,
}: {
  icon: typeof Cpu;
  label: string;
  value: ReactNode;
  detail?: ReactNode;
  tone?: Tone;
  tab: MonitoringTab;
}) {
  const { setTab } = useMonitoring();
  return (
    <button
      type="button"
      onClick={() => setTab(tab)}
      className={cn(
        "flex min-w-0 flex-col gap-1 rounded-xl border bg-card px-3.5 py-3 text-left transition-colors hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
        tone === "nok" && "border-red-500/40 bg-red-500/[0.07]",
        tone === "warn" && "border-amber-500/30",
      )}
    >
      <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
        <Icon
          className={cn("h-3.5 w-3.5 shrink-0", tone === "nok" ? "text-red-400" : tone === "warn" ? "text-amber-400" : "")}
          aria-hidden
        />
        <span className="truncate">{label}</span>
      </div>
      <div
        className={cn(
          "truncate text-xl font-semibold tabular-nums tracking-tight",
          tone === "nok" && "text-red-300",
          tone === "warn" && "text-amber-200",
        )}
      >
        {value}
      </div>
      {detail && <div className="line-clamp-2 text-[11px] leading-snug text-muted-foreground">{detail}</div>}
    </button>
  );
}

function KpiGrid() {
  const { t } = useLocalization();
  const summary = useMonitoringSummary();
  if (summary.isLoading) {
    return (
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        {Array.from({ length: 8 }).map((_, i) => (
          <LoadingBlock key={i} height={88} />
        ))}
      </div>
    );
  }
  const kpis = summary.data?.kpis;
  if (!kpis) return null;
  const findings = summary.data?.findings ?? [];
  const areaTone = (area: string): Tone => {
    const own = findings.filter((f) => f.area === area);
    if (own.some((f) => f.severity === "critical")) return "nok";
    if (own.some((f) => f.severity === "warning")) return "warn";
    return "neutral";
  };

  const { pods, cameras, pipeline, http, errors, backend } = kpis;
  const verdict = pipeline.capture_to_verdict_1h;
  const lagTone: Tone =
    (backend.lag_max_5m_ms ?? 0) >= 1000 ? "nok" : (backend.lag_max_5m_ms ?? 0) >= 300 ? "warn" : "neutral";

  return (
    <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
      <KpiTile
        icon={Cpu}
        tab="pods"
        label={t("monitoring.kpi.pods")}
        // the findings decide: an empty pool at night (scaled to zero) is calm, with pizzas coming it is red
        tone={areaTone("pods")}
        value={
          pods.total === 0
            ? t("monitoring.kpi.podsNone")
            : t("monitoring.kpi.podsValue", { healthy: pods.healthy, total: pods.total })
        }
        detail={t("monitoring.kpi.podsDetail", {
          requests: formatCount(pods.requests_15m),
          failures: formatCount(pods.failures_15m),
          p95: formatMs(pods.p95_ms_15m),
        })}
      />
      <KpiTile
        icon={Camera}
        tab="cameras"
        label={t("monitoring.kpi.cameras")}
        tone={cameras.error || cameras.offline > 0 ? "nok" : areaTone("cameras")}
        value={cameras.error ? DASH : t("monitoring.kpi.camerasValue", { online: cameras.online, watched: cameras.watched })}
        detail={
          cameras.error ??
          t("monitoring.kpi.camerasDetail", { offline: cameras.offline, unknown: cameras.unknown, total: cameras.total })
        }
      />
      <KpiTile
        icon={PizzaIcon}
        tab="pipeline"
        label={t("monitoring.kpi.captures")}
        tone={areaTone("integrations") === "warn" ? "warn" : "neutral"}
        value={
          <span>
            {formatCount(pipeline.captured_1h)}
            {pipeline.last_capture_at ? (
              <span className="ml-2 text-xs font-normal text-muted-foreground">
                {t("monitoring.kpi.lastCapture", { ago: "" }).trim()} <Ago ts={pipeline.last_capture_at} />
              </span>
            ) : null}
          </span>
        }
        detail={
          pipeline.captured_1h_usual !== null && pipeline.captured_1h_usual !== undefined
            ? t("monitoring.kpi.capturesDetail", {
                usual: formatCount(pipeline.captured_1h_usual),
                orders: formatCount(pipeline.orders_1h),
                ordersUsual: formatCount(pipeline.orders_1h_usual ?? 0),
              })
            : t("monitoring.kpi.capturesDetailNoBaseline", { orders: formatCount(pipeline.orders_1h) })
        }
      />
      <KpiTile
        icon={Timer}
        tab="pipeline"
        label={t("monitoring.kpi.verdict")}
        tone={findings.some((f) => f.code === "verdict_slow") ? "warn" : "neutral"}
        value={`p95 ${formatMs(verdict?.p95)}`}
        detail={t("monitoring.kpi.verdictDetail", { p50: formatMs(verdict?.p50), n: formatCount(verdict?.n ?? 0) })}
      />
      <KpiTile
        icon={Hourglass}
        tab="pipeline"
        label={t("monitoring.kpi.stuck")}
        tone={pipeline.stuck_1h > 0 || pipeline.uncaptured > 0 ? "nok" : "neutral"}
        value={formatCount(pipeline.stuck_1h + pipeline.uncaptured)}
        detail={t("monitoring.kpi.stuckDetail", {
          stuck: formatCount(pipeline.stuck_1h),
          uncaptured: formatCount(pipeline.uncaptured),
        })}
      />
      <KpiTile
        icon={Globe}
        tab="requests"
        label={t("monitoring.kpi.requests")}
        tone={areaTone("http")}
        value={formatCount(http.requests_15m)}
        detail={t("monitoring.kpi.requestsDetail", {
          errors: formatCount(http.errors_15m),
          clientErrors: formatCount(http.client_errors_15m),
          p95: formatMs(http.p95_ms_15m),
        })}
      />
      <KpiTile
        icon={TriangleAlert}
        tab="events"
        label={t("monitoring.kpi.errors")}
        tone={errors.errors_15m > 0 ? "nok" : errors.errors_60m > 0 ? "warn" : "neutral"}
        value={formatCount(errors.errors_60m)}
        detail={t("monitoring.kpi.errorsDetail", {
          warnings: formatCount(errors.warnings_60m),
          recent: formatCount(errors.errors_15m),
        })}
      />
      <KpiTile
        icon={ServerCog}
        tab="backend"
        label={t("monitoring.kpi.backend")}
        tone={lagTone === "neutral" ? areaTone("backend") : lagTone}
        value={t("monitoring.kpi.backendValue", { uptime: formatDuration(backend.uptime_s) })}
        detail={t("monitoring.kpi.backendDetail", {
          lag: formatMs(backend.lag_max_5m_ms),
          rss: formatMb(backend.rss_mb),
          cpu: backend.cpu_pct === null ? DASH : `${Math.round(backend.cpu_pct)} %`,
        })}
      />
    </div>
  );
}

// --- charts -------------------------------------------------------------------------------------------------
function FlowChart() {
  const { t } = useLocalization();
  const { window } = useMonitoring();
  const pipeline = useMonitoringPipeline();
  const data = pipeline.data;
  const series = data
    ? [
        { key: "ok", label: t("monitoring.verdict.ok"), color: VERDICT_COLORS.ok, values: data.series["verdict.ok"], type: "bar" as const, stackId: "v" },
        { key: "nok", label: t("monitoring.verdict.nok"), color: VERDICT_COLORS.nok, values: data.series["verdict.nok"], type: "bar" as const, stackId: "v" },
        {
          key: "photo",
          label: t("monitoring.verdict.photo"),
          color: VERDICT_COLORS.invalid_photo,
          values: data.series["verdict.invalid_photo"].map((v, i) => (v ?? 0) + (data.series["verdict.no_pizza"][i] ?? 0)),
          type: "bar" as const,
          stackId: "v",
        },
        { key: "pending", label: t("monitoring.verdict.pending"), color: VERDICT_COLORS.pending, values: data.series["verdict.pending"], type: "bar" as const, stackId: "v" },
        { key: "missing", label: t("monitoring.verdict.missing"), color: VERDICT_COLORS.missing, values: data.series["verdict.missing"], type: "bar" as const, stackId: "v" },
        { key: "orders", label: t("monitoring.series.orders"), color: SERIES[0], values: data.series.orders },
        ...(data.baseline
          ? [{ key: "usual", label: t("monitoring.series.usualCaptured"), color: NEUTRAL.baseline, values: data.baseline.captured, dashed: true }]
          : []),
      ]
    : [];
  return (
    <SectionCard
      title={t("monitoring.overview.flow")}
      description={t("monitoring.overview.flowDescription", { window: t(`monitoring.window.long.${window}`) })}
      actions={<FetchingDot active={pipeline.isFetching} />}
    >
      {pipeline.isLoading ? (
        <LoadingBlock height={220} />
      ) : pipeline.isError && !data ? (
        <ErrorBlock message={errorMessage(pipeline.error)} onRetry={() => pipeline.refetch()} />
      ) : data ? (
        <TimeSeriesChart t={data.t} stepMs={data.step_ms} window={window} series={series} height={220} integer
          format={(v) => formatCount(v)} emptyText={t("monitoring.noData")} />
      ) : null}
    </SectionCard>
  );
}

function VerdictLatencyChart() {
  const { t } = useLocalization();
  const { window } = useMonitoring();
  const pipeline = useMonitoringPipeline();
  const data = pipeline.data;
  return (
    <SectionCard
      title={t("monitoring.overview.latency")}
      description={t("monitoring.overview.latencyDescription", { window: t(`monitoring.window.long.${window}`) })}
      actions={<FetchingDot active={pipeline.isFetching} />}
    >
      {pipeline.isLoading ? (
        <LoadingBlock height={220} />
      ) : pipeline.isError && !data ? (
        <ErrorBlock message={errorMessage(pipeline.error)} onRetry={() => pipeline.refetch()} />
      ) : data ? (
        <TimeSeriesChart
          t={data.t}
          stepMs={data.step_ms}
          window={window}
          height={220}
          format={formatMs}
          tickFormat={formatMsTick}
          emptyText={t("monitoring.noData")}
          series={[
            { key: "p95", label: t("monitoring.series.p95"), color: SERIES[0], values: data.series["capture_to_verdict_ms.p95"] },
            { key: "p50", label: t("monitoring.series.p50"), color: SERIES_LIGHT, values: data.series["capture_to_verdict_ms.p50"], dashed: true },
          ]}
        />
      ) : null}
    </SectionCard>
  );
}

function PodLatencyChart() {
  const { t } = useLocalization();
  const { window } = useMonitoring();
  const metrics = useMonitoringMetrics();
  const data = metrics.data;
  const hist = data?.hists["pod.request_ms"];
  return (
    <SectionCard
      title={t("monitoring.overview.pods")}
      description={t("monitoring.overview.podsDescription", { window: t(`monitoring.window.long.${window}`) })}
      actions={<FetchingDot active={metrics.isFetching} />}
    >
      {metrics.isLoading ? (
        <LoadingBlock height={220} />
      ) : metrics.isError && !data ? (
        <ErrorBlock message={errorMessage(metrics.error)} onRetry={() => metrics.refetch()} />
      ) : data ? (
        <div className="flex flex-col gap-3">
          <TimeSeriesChart
            t={data.t}
            stepMs={data.step_ms}
            window={window}
            height={170}
            format={formatMs}
            tickFormat={formatMsTick}
            emptyText={t("monitoring.noData")}
            series={[
              { key: "p95", label: t("monitoring.series.p95"), color: SERIES[0], values: hist?.p95 ?? [] },
              { key: "p50", label: t("monitoring.series.p50"), color: SERIES_LIGHT, values: hist?.p50 ?? [], dashed: true },
            ]}
          />
          <TimeSeriesChart
            t={data.t}
            stepMs={data.step_ms}
            window={window}
            height={90}
            integer
            format={(v) => formatCount(v)}
            series={[
              {
                key: "failures",
                label: t("monitoring.series.failures"),
                color: STATUS.critical,
                type: "bar",
                values: (data.counters["pod.fail"] ?? data.t.map(() => 0)).map(
                  (v, i) => v + (data.counters["pod.timeout"]?.[i] ?? 0),
                ),
              },
            ]}
          />
        </div>
      ) : null}
    </SectionCard>
  );
}

function EventsChart() {
  const { t } = useLocalization();
  const { window } = useMonitoring();
  const metrics = useMonitoringMetrics();
  const data = metrics.data;
  const zeros = data?.t.map(() => 0) ?? [];
  return (
    <SectionCard
      title={t("monitoring.overview.errors")}
      description={t("monitoring.overview.errorsDescription", { window: t(`monitoring.window.long.${window}`) })}
      actions={<FetchingDot active={metrics.isFetching} />}
    >
      {metrics.isLoading ? (
        <LoadingBlock height={220} />
      ) : metrics.isError && !data ? (
        <ErrorBlock message={errorMessage(metrics.error)} onRetry={() => metrics.refetch()} />
      ) : data ? (
        <TimeSeriesChart
          t={data.t}
          stepMs={data.step_ms}
          window={window}
          height={220}
          integer
          format={(v) => formatCount(v)}
          emptyText={t("monitoring.noData")}
          series={[
            { key: "error", label: t("monitoring.level.error"), color: LEVEL_COLORS.error, type: "bar", stackId: "e", values: data.counters["events.error"] ?? zeros },
            { key: "warning", label: t("monitoring.level.warning"), color: LEVEL_COLORS.warning, type: "bar", stackId: "e", values: data.counters["events.warning"] ?? zeros },
            { key: "info", label: t("monitoring.level.info"), color: LEVEL_COLORS.info, type: "bar", stackId: "e", values: data.counters["events.info"] ?? zeros, hidden: true },
          ]}
        />
      ) : null}
    </SectionCard>
  );
}

// --- live feeds ---------------------------------------------------------------------------------------------
const VERDICT_TONE: Record<Verdict, string> = {
  ok: "text-muted-foreground",
  nok: "text-orange-300",
  invalid_photo: "text-amber-300",
  no_pizza: "text-amber-300",
  pending: "text-muted-foreground",
  missing: "text-red-300",
};

function VerdictLabel({ verdict }: { verdict: Verdict }) {
  const { t } = useLocalization();
  return (
    <span className={cn("whitespace-nowrap text-xs font-medium", VERDICT_TONE[verdict])}>
      {t(`monitoring.verdict.${verdict}`)}
    </span>
  );
}

function RecentCaptures() {
  const { t } = useLocalization();
  const { setTab } = useMonitoring();
  const pipeline = useMonitoringPipeline();
  const names = new Map((pipeline.data?.branches_meta ?? []).map((b) => [b.id, b.name]));
  const items = (pipeline.data?.recent ?? []).slice(0, 8);
  return (
    <SectionCard
      title={t("monitoring.overview.recentCaptures")}
      description={t("monitoring.overview.recentCapturesDescription")}
      actions={
        <button type="button" className="text-xs text-muted-foreground hover:text-foreground" onClick={() => setTab("pipeline")}>
          {t("monitoring.overview.showAll")}
        </button>
      }
      contentClassName="p-0"
    >
      {pipeline.isLoading ? (
        <div className="p-4"><LoadingBlock height={200} /></div>
      ) : items.length === 0 ? (
        <div className="p-4"><EmptyBlock>{t("monitoring.overview.noCaptures")}</EmptyBlock></div>
      ) : (
        <ul className="divide-y">
          {items.map((pizza) => (
            <CaptureRow key={`${pizza.id}-${pizza.capture_at}`} pizza={pizza} branch={names.get(pizza.branch_id ?? "") ?? null} />
          ))}
        </ul>
      )}
    </SectionCard>
  );
}

function CaptureRow({ pizza, branch }: { pizza: PipelinePizza; branch: string | null }) {
  const { t } = useLocalization();
  return (
    <li className="flex items-center gap-3 px-4 py-2">
      <span className="w-20 shrink-0 font-mono text-[11px] tabular-nums text-muted-foreground">{formatClock(pizza.capture_at)}</span>
      <div className="min-w-0 flex-1">
        <Link to={`/pizzas/${pizza.id}`} className="block truncate text-sm hover:underline">
          {pizza.name ?? pizza.id}
        </Link>
        <div className="truncate text-[11px] text-muted-foreground">{branch ?? t("monitoring.unknownBranch")}</div>
      </div>
      <div className="flex shrink-0 flex-col items-end gap-0.5">
        <VerdictLabel verdict={pizza.verdict} />
        <span className="font-mono text-[11px] tabular-nums text-muted-foreground">
          {pizza.capture_to_verdict_ms !== null ? formatMs(pizza.capture_to_verdict_ms) : DASH}
        </span>
      </div>
    </li>
  );
}

function RecentEvents() {
  const { t } = useLocalization();
  const { setTab } = useMonitoring();
  const live = useMonitoringLive();
  const items = (live.data?.events ?? []).slice(0, 8);
  return (
    <SectionCard
      title={t("monitoring.overview.recentEvents")}
      description={t("monitoring.overview.recentEventsDescription")}
      actions={
        <button type="button" className="text-xs text-muted-foreground hover:text-foreground" onClick={() => setTab("events")}>
          {t("monitoring.overview.showAll")}
        </button>
      }
      contentClassName="p-0"
    >
      {live.isLoading ? (
        <div className="p-4"><LoadingBlock height={200} /></div>
      ) : items.length === 0 ? (
        <div className="p-4"><EmptyBlock>{t("monitoring.overview.noEvents")}</EmptyBlock></div>
      ) : (
        <ul className="divide-y">
          {items.map((event) => (
            <li key={event.id} className="flex items-start gap-3 px-4 py-2">
              <span className="w-20 shrink-0 pt-0.5 font-mono text-[11px] tabular-nums text-muted-foreground">{formatClock(event.ts)}</span>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-1.5">
                  <LevelBadge level={event.level} />
                  <span className="font-mono text-[11px] text-muted-foreground">{event.source}.{event.code}</span>
                  {event.repeats > 0 && <span className="text-[11px] text-muted-foreground">×{event.repeats + 1}</span>}
                </div>
                <div className="mt-0.5 line-clamp-2 break-words text-xs">{event.message}</div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </SectionCard>
  );
}

